import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Check,
  Copy,
  ImageUp,
  Loader2,
  UserPlus,
  Users,
} from 'lucide-react';
import { apiFetch, API_BASE } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';

/**
 * MI ORGANIZACIÓN (/organizacion) — el gestor en autoservicio del tenant.
 * Identidad (nombre, logo, atribución, color — visten el login ?org= y las
 * superficies vía ADR-0003) y usuarios propios, dentro de los asientos de
 * facilitador del plan. Para facilitadores; el superadmin tiene /admin.
 */

interface MiOrg {
  id: string;
  nombre: string;
  slug: string;
  plan_codigo: string | null;
  plan_nombre: string | null;
  facilitadores_activos: number;
  facilitadores_tope: number | null;
  login_url: string;
  logo_url: string | null;
  atribucion: string | null;
  color: string | null;
}

interface UsuarioOrg {
  id: string;
  nombre: string;
  email: string | null;
  rol_global: string;
  activo: boolean;
}

export function MiOrgPage() {
  useSurfaceTheme('bolsillo', 'light');
  const qc = useQueryClient();
  const yo = useAuthStore((s) => s.user);

  const org = useQuery({
    queryKey: ['mi-org'],
    queryFn: () => apiFetch<MiOrg>('/mi-org'),
  });
  const usuarios = useQuery({
    queryKey: ['mi-org', 'usuarios'],
    queryFn: () => apiFetch<UsuarioOrg[]>('/mi-org/usuarios'),
  });
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['mi-org'] });

  const [nombre, setNombre] = useState('');
  const [atribucion, setAtribucion] = useState('');
  const [color, setColor] = useState('#2563EB');
  const [copiado, setCopiado] = useState(false);
  useEffect(() => {
    if (org.data) {
      setNombre(org.data.nombre);
      setAtribucion(org.data.atribucion ?? '');
      setColor(org.data.color ?? '#2563EB');
    }
  }, [org.data]);

  const guardar = useMutation({
    mutationFn: () =>
      apiFetch<MiOrg>('/mi-org', {
        method: 'PATCH',
        json: { nombre: nombre.trim(), atribucion, color },
      }),
    onSuccess: invalidar,
  });

  const subirLogo = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const token = useAuthStore.getState().token;
      const r = await fetch(`${API_BASE}/mi-org/logo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
        credentials: 'include',
      });
      if (!r.ok) throw await r.json().catch(() => ({ detail: 'No se pudo subir.' }));
      return (await r.json()) as MiOrg;
    },
    onSuccess: invalidar,
  });

  const patchUsuario = useMutation({
    mutationFn: (v: { id: string; cambios: Record<string, unknown> }) =>
      apiFetch<UsuarioOrg>(`/mi-org/usuarios/${v.id}`, { method: 'PATCH', json: v.cambios }),
    onSuccess: invalidar,
  });

  const d = org.data;
  const loginCompleto = d ? `${window.location.origin}${d.login_url}` : '';

  if (org.isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base px-6 text-center">
        <Building2 className="h-10 w-10 text-content-muted" aria-hidden="true" />
        <p className="font-sans text-base text-content">
          Tu organización la administra quien la facilita.
        </p>
        <Link to="/entrar" className="font-sans text-sm text-primary underline">
          Volver
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base pb-16">
      <header className="border-b border-border bg-surface-raised px-4 py-3 sm:px-6">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Link
            to="/procesos"
            className="flex min-h-touch items-center gap-1.5 rounded-md font-sans text-sm text-content-muted hover:text-content"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Procesos
          </Link>
          <h1 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            <Building2 className="h-5 w-5" aria-hidden="true" />
            Mi organización
          </h1>
          {d?.plan_nombre && (
            <span className="rounded-full bg-surface-sunken px-2.5 py-0.5 font-sans text-xs font-semibold text-content-muted">
              Plan {d.plan_nombre}
            </span>
          )}
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6">
        {/* ── Identidad ── */}
        <section className="rounded-xl border border-border bg-surface-raised p-4">
          <h2 className="font-display text-sm font-semibold text-content">Identidad del tenant</h2>
          <p className="mt-1 font-sans text-xs text-content-muted">
            Tu nombre, logo y color visten la puerta de ingreso de tu gente y tus
            constancias. Compartí tu link de login para que entren con tu marca.
          </p>
          <form
            className="mt-4 grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (nombre.trim().length >= 2) guardar.mutate();
            }}
          >
            <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
              Nombre de la organización
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              />
            </label>
            <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
              Atribución (pie de constancias, opcional)
              <input
                value={atribucion}
                onChange={(e) => setAtribucion(e.target.value)}
                placeholder="Con el apoyo de…"
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              />
            </label>
            <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
              Color de acento
              <span className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-border bg-surface-base"
                  aria-label="Color de acento del tenant"
                />
                <code className="font-sans text-xs text-content-muted">{color}</code>
              </span>
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={guardar.isPending || nombre.trim().length < 2}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-40"
              >
                {guardar.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : guardar.isSuccess ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : null}
                Guardar identidad
              </button>
            </div>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            {d?.logo_url ? (
              <img
                src={d.logo_url}
                alt={`Logo de ${d.nombre}`}
                className="h-14 w-auto rounded-md border border-border bg-white p-1"
              />
            ) : (
              <span className="font-sans text-xs text-content-muted">Aún sin logo</span>
            )}
            <label className="inline-flex min-h-touch cursor-pointer items-center gap-1.5 rounded-lg border border-border-strong px-3 py-2 font-sans text-sm font-semibold text-content transition hover:bg-surface-sunken">
              {subirLogo.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ImageUp className="h-4 w-4" aria-hidden="true" />
              )}
              {d?.logo_url ? 'Cambiar logo' : 'Subir logo'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) subirLogo.mutate(f);
                  e.target.value = '';
                }}
              />
            </label>
            {subirLogo.isError && (
              <p className="font-sans text-xs text-error">
                {(subirLogo.error as { detail?: string })?.detail ?? 'No se pudo subir.'}
              </p>
            )}
          </div>

          {d && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2">
              <span className="font-sans text-xs text-content-muted">Login con tu marca:</span>
              <code className="min-w-0 flex-1 truncate font-sans text-xs text-content">
                {loginCompleto}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(loginCompleto).then(() => {
                    setCopiado(true);
                    setTimeout(() => setCopiado(false), 2000);
                  });
                }}
                className="inline-flex items-center gap-1 rounded-md border border-border-strong px-2 py-1 font-sans text-xs font-semibold text-content hover:bg-surface-sunken"
              >
                {copiado ? (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          )}
        </section>

        {/* ── Usuarios ── */}
        <section className="rounded-xl border border-border bg-surface-raised p-4">
          <h2 className="flex items-center gap-2 font-display text-sm font-semibold text-content">
            <Users className="h-4 w-4" aria-hidden="true" />
            Usuarios
            {d && (
              <span className="font-sans text-xs font-normal text-content-muted">
                {d.facilitadores_activos}
                {d.facilitadores_tope != null ? `/${d.facilitadores_tope}` : ''} facilitadores
              </span>
            )}
          </h2>

          <AltaUsuarioOrg onCreado={invalidar} />

          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[520px] font-sans text-sm">
              <thead className="bg-surface-sunken text-left text-xs uppercase tracking-wide text-content-muted">
                <tr>
                  <th className="px-3 py-2">Persona</th>
                  <th className="px-3 py-2">Rol</th>
                  <th className="px-3 py-2">Activo</th>
                </tr>
              </thead>
              <tbody>
                {(usuarios.data ?? []).map((u) => (
                  <tr key={u.id} className={u.activo ? 'border-t border-border' : 'border-t border-border opacity-50'}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-content">{u.nombre}</span>
                      {u.email && <span className="ml-2 text-xs text-content-muted">{u.email}</span>}
                      {u.id === yo?.id && (
                        <span className="ml-2 text-xs text-primary">(vos)</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {u.rol_global === 'superadmin' ? (
                        <span className="text-xs text-content-muted">superadmin</span>
                      ) : (
                        <select
                          value={u.rol_global}
                          onChange={(e) =>
                            patchUsuario.mutate({ id: u.id, cambios: { rol_global: e.target.value } })
                          }
                          className="rounded border border-border-strong bg-surface-raised px-2 py-1 text-sm text-content"
                          aria-label={`Rol de ${u.nombre}`}
                        >
                          <option value="miembro">participante</option>
                          <option value="facilitador">facilitador</option>
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={u.activo}
                        disabled={u.id === yo?.id || u.rol_global === 'superadmin'}
                        onChange={(e) =>
                          patchUsuario.mutate({ id: u.id, cambios: { activo: e.target.checked } })
                        }
                        className="h-4 w-4"
                        aria-label={`${u.nombre} activo`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {patchUsuario.isError && (
            <p className="mt-2 font-sans text-xs text-error">
              {(patchUsuario.error as { detail?: string })?.detail ?? 'No se pudo aplicar.'}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}

function AltaUsuarioOrg({ onCreado }: { onCreado: () => void }) {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState('miembro');

  const crear = useMutation({
    mutationFn: () =>
      apiFetch<UsuarioOrg>('/mi-org/usuarios', {
        method: 'POST',
        json: { nombre: nombre.trim(), email: email.trim(), rol_global: rol },
      }),
    onSuccess: () => {
      setNombre('');
      setEmail('');
      onCreado();
    },
  });
  const valido = nombre.trim().length >= 2 && /\S+@\S+\.\S+/.test(email);

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (valido) crear.mutate();
      }}
    >
      <label className="flex min-w-[160px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
        Nombre
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
        />
      </label>
      <label className="flex min-w-[200px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
        Correo (entra con Google)
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
        />
      </label>
      <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
        Rol
        <select
          value={rol}
          onChange={(e) => setRol(e.target.value)}
          className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
        >
          <option value="miembro">participante</option>
          <option value="facilitador">facilitador</option>
        </select>
      </label>
      <button
        type="submit"
        disabled={!valido || crear.isPending}
        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-40"
      >
        {crear.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <UserPlus className="h-4 w-4" aria-hidden="true" />
        )}
        Agregar
      </button>
      {crear.isError && (
        <p className="w-full font-sans text-xs text-error">
          {(crear.error as { detail?: string })?.detail ?? 'No se pudo crear.'}
        </p>
      )}
    </form>
  );
}
